import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import styles from "./Calculator.module.css";

// Assumptions behind the estimate. Kept conservative and shown to the visitor.
const TARGET_LOAD = 85; // % truck fill reachable with load consolidation
const ROUTE_SAVING = 0.1; // 10% fewer km from route optimization (typical range 8–15%)
const CO2_PER_KM = 0.9; // kg CO2 per km, average diesel truck

const fields = [
  { key: "trips", label: "Deliveries / trips per month", min: 10, max: 2000, step: 10, unit: "" },
  { key: "km", label: "Average route distance", min: 5, max: 800, step: 5, unit: "km" },
  { key: "costPerKm", label: "Cost per km (fuel, driver, tolls, wear)", min: 0.3, max: 4, step: 0.05, unit: "€" },
  { key: "load", label: "Average truck load utilization", min: 20, max: 100, step: 1, unit: "%" },
];

const eur = (n) =>
  new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const num = (n) => new Intl.NumberFormat("el-GR", { maximumFractionDigits: 0 }).format(n);

const estimate = ({ trips, km, costPerKm, load }) => {
  const currentKm = trips * km;
  const currentCost = currentKm * costPerKm;

  const optimizedTrips = load < TARGET_LOAD ? (trips * load) / TARGET_LOAD : trips;
  const optimizedKm = optimizedTrips * km * (1 - ROUTE_SAVING);
  const optimizedCost = optimizedKm * costPerKm;

  const kmSaved = currentKm - optimizedKm;
  return {
    currentCost,
    monthly: currentCost - optimizedCost,
    yearly: (currentCost - optimizedCost) * 12,
    percent: currentCost ? ((currentCost - optimizedCost) / currentCost) * 100 : 0,
    tripsSaved: trips - optimizedTrips,
    kmSaved,
    co2Tonnes: (kmSaved * CO2_PER_KM * 12) / 1000,
  };
};

const Calculator = () => {
  const [values, setValues] = useState({ trips: 200, km: 120, costPerKm: 1.4, load: 70 });
  const result = estimate(values);

  const update = (key, raw) => {
    const value = parseFloat(raw);
    setValues((prev) => ({ ...prev, [key]: Number.isNaN(value) ? 0 : value }));
  };

  const contactState = {
    topic: "Transport Optimization",
    message:
      `Hi, I used the savings calculator on your site.\n\n` +
      `Trips per month: ${values.trips}\n` +
      `Average route: ${values.km} km\n` +
      `Cost per km: €${values.costPerKm}\n` +
      `Load utilization: ${values.load}%\n\n` +
      `Estimated saving: ${eur(result.yearly)} / year. I'd like to discuss this.`,
  };

  return (
    <motion.div
      className={styles.container}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <h2 className={styles.title}>Transport Savings Calculator</h2>
      <p className={styles.subtitle}>
        How much are half-empty trucks and unplanned routes costing you? Adjust the numbers to match
        your fleet and see a quick estimate.
      </p>

      <div className={styles.layout}>
        <div className={styles.inputs}>
          {fields.map((f) => (
            <div key={f.key} className={styles.field}>
              <label htmlFor={f.key}>
                <span>{f.label}</span>
                <span className={styles.inputWrap}>
                  {f.unit === "€" && <em>€</em>}
                  <input
                    id={f.key}
                    type="number"
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={values[f.key]}
                    onChange={(e) => update(f.key, e.target.value)}
                  />
                  {f.unit && f.unit !== "€" && <em>{f.unit}</em>}
                </span>
              </label>
              <input
                type="range"
                aria-label={f.label}
                min={f.min}
                max={f.max}
                step={f.step}
                value={values[f.key]}
                onChange={(e) => update(f.key, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div className={styles.results} aria-live="polite">
          <p className={styles.resultLabel}>Estimated yearly savings</p>
          <p className={styles.big}>{eur(Math.max(0, result.yearly))}</p>
          <p className={styles.percent}>
            {result.percent.toFixed(0)}% of your current {eur(result.currentCost)} / month transport cost
          </p>

          <div className={styles.stats}>
            <div>
              <strong>{eur(Math.max(0, result.monthly))}</strong>
              <span>saved per month</span>
            </div>
            <div>
              <strong>{num(result.tripsSaved)}</strong>
              <span>fewer trips / month</span>
            </div>
            <div>
              <strong>{num(result.kmSaved)}</strong>
              <span>fewer km / month</span>
            </div>
            <div>
              <strong>{result.co2Tonnes.toFixed(1)} t</strong>
              <span>less CO₂ / year</span>
            </div>
          </div>

          <Link to="/contact" state={contactState} className={styles.cta}>
            Get a real analysis of your data →
          </Link>
        </div>
      </div>

      <p className={styles.assumptions}>
        Rough estimate, not a quote. Assumes load consolidation up to {TARGET_LOAD}% truck utilization,
        about {ROUTE_SAVING * 100}% shorter routes through route optimization, and {CO2_PER_KM} kg CO₂ per
        km. Real savings depend on your routes, delivery windows and fleet, which is exactly what a
        consultation looks at.
      </p>
    </motion.div>
  );
};

export default Calculator;
